import mixpanel from 'mixpanel-browser'
import { useEffect, useState } from 'react'

import { AnimatedText } from './AnimatedText'
import { Button } from './Button'
import { Input } from './Input'

import { useFormValidation } from '../hooks/UseFormValidation'

interface ContentStartProps {
  error: string
  handleName: (value: string) => void
}

const ANIMATION_INTERVAL = 2000

const WISDOM_TYPES = [
  { word: 'Concierge', example: 'Personal Guide' },
  { word: 'Assistant', example: 'Daily Helper' },
  { word: 'Mentor', example: 'Career Support' },
  { word: 'Coach', example: 'Fitness Trainer' },
  { word: 'Guide', example: 'Travel Advisor' },
  { word: 'Oracle', example: 'Self Discovery' },
  { word: 'Tutor', example: 'Math Helper' },
  { word: 'Sage', example: 'Thich Nhat Hanh' },
  { word: 'Expert', example: 'Tech Specialist' },
  { word: 'Teacher', example: 'Aristotle' },
  { word: 'Advisor', example: 'Francis Finances' },
  { word: 'Consultant', example: 'Software Architect' },
  { word: 'Specialist', example: 'Coding Ninja' },
]

export function ContentStart({ handleName, error }: ContentStartProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const words = WISDOM_TYPES.map((type) => type.word)
  const currentExample = WISDOM_TYPES[currentIndex].example

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % WISDOM_TYPES.length)
    }, ANIMATION_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (error) {
      formValidator.setCustomError(error)
    }
  }, [error])

  useEffect(() => {
    mixpanel.track('EnterYourNameScreenViewed')
  }, [])

  const formValidator = useFormValidation()

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleName(formValidator.value)
  }

  return (
    <div className='mx-auto max-w-4xl flex-grow flex items-center'>
      <div className='text-center'>
        <h1 className='animate-slidein300 font-serif opacity-0 text-balance text-4xl font-medium tracking-tight text-dw-black sm:text-[54px] font-semibold'>
          Create your <span className='text-dw-gold'>AI</span>
          <br />
          <br />
          <AnimatedText
            words={words}
            interval={ANIMATION_INTERVAL}
            animation='photon'
            className='text-dw-gold'
          />
        </h1>
        <div className='animate-slidein700 opacity-0 mt-16 flex items-center justify-center gap-x-6 '>
          <form
            onSubmit={handleSubmit}
            className='w-full lg:col-span-5 lg:pt-2'
          >
            <div className='flex gap-x-4 flex-col max-w-2xl mx-auto'>
              <div>
                <label
                  htmlFor='name'
                  className='text-2xl sm:text-4xl font-serif tracking-tight font-semibold'
                >
                  What will you name this wisdom?
                </label>
                <div className='text-sm text-gray-600 mt-2 italic'>
                  <strong>wisdom</strong> /ˈwɪzdəm/ <em>noun</em>: your
                  personalized, shareable AI concierge
                </div>
              </div>
              <div className='h-[50px] mt-9 p-1 bg-white flex gap-x-2 rounded-[14px] outline outline-1 -outline-offset-1 outline-gray-400 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-gray-600'>
                <Input
                  id='name'
                  name='name'
                  inputMode='text'
                  autoCapitalize='words'
                  placeholder={`Ex: ${currentExample}`}
                  value={formValidator.value}
                  onInputChange={formValidator.handleChange}
                  className='outline-none'
                />

                <Button
                  type='submit'
                  title='Continue'
                  className='bg-dw-yellow-accent hover:bg-dw-gold focus-visible:outline-dw-gold'
                />
              </div>
              <div className='h-5'>
                {formValidator.error && (
                  <div className='text-red-500 text-sm text-left'>
                    {formValidator.error}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
